import React from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { ThemeProvider } from './context/ThemeContext';
import Sidebar from './components/Sidebar/Sidebar';
import AppRoutes from './routes/AppRoutes';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import LockoutScreen from './components/Lockout/LockoutScreen';
import { isOAuthPopupWindow } from './utils/composioOAuthPopup';
import styles from './App.module.scss';

const appEnvironment = (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development').toLowerCase();
const environmentBadgeLabels = {
  development: 'Development',
  stage: 'Stage'
};
const environmentBadgeLabel = environmentBadgeLabels[appEnvironment] || null;

const AppContent = () => {
  const { loading } = useUser();
  const { lockout } = usePermissions();
  const location = useLocation();

  // Partner / company / user deactivated (Partner tier guide §9): block the whole
  // app — no sidebar, no routes, so nothing keeps polling.
  if (lockout && location.pathname !== '/login') {
    return <LockoutScreen kind={lockout} />;
  }

  // Don't show loading screen on login page
  if (loading && location.pathname !== '/login') {
    return (
      <>
        {environmentBadgeLabel && (
          <div className={styles.environmentBadge}>{environmentBadgeLabel}</div>
        )}
        <LoadingScreen />
      </>
    );
  }

  // Hide sidebar on login page and in the one-click OAuth login popup
  const showSidebar =
    location.pathname !== '/login' &&
    !(location.pathname === '/oauth/callback' && isOAuthPopupWindow());

  return (
    <>
      {environmentBadgeLabel && (
        <div className={styles.environmentBadge}>{environmentBadgeLabel}</div>
      )}
      <div className={styles.app}>
        {showSidebar && <Sidebar />}
        <div className={styles.content}>
          <AppRoutes />
        </div>
      </div>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <PermissionsProvider>
          <Router>
            <AppContent />
          </Router>
        </PermissionsProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
